import React, { useState, useEffect } from 'react';

const AddContactsModal = ({ onClose, onSave, alumni, existingContactIds }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [filteredAlumni, setFilteredAlumni] = useState([]);

  // Advanced filter states
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedCompanySize, setSelectedCompanySize] = useState('');
  const [selectedProjectSize, setSelectedProjectSize] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedTechAlignment, setSelectedTechAlignment] = useState('');
  const [selectedLastContact, setSelectedLastContact] = useState('');

  // Helper for last contact filter
  const isLastContactMatch = (alumnusLastContact, filterValue) => {
    if (!filterValue) return true;
    if (!alumnusLastContact) return false;
    const now = new Date();
    const lastContactDate = new Date(alumnusLastContact);
    switch (filterValue) {
      case 'This Week':
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        return lastContactDate >= weekAgo;
      case 'This Month':
        const monthAgo = new Date(now);
        monthAgo.setMonth(now.getMonth() - 1);
        return lastContactDate >= monthAgo;
      case 'This Year':
        const yearAgo = new Date(now);
        yearAgo.setFullYear(now.getFullYear() - 1);
        return lastContactDate >= yearAgo;
      default:
        return true;
    }
  };


  // Initialize filtered alumni
  useEffect(() => {
    // Filter out alumni that are already in the campaign
    const availableAlumni = alumni.filter(
      alumnus => !existingContactIds.includes(alumnus.emp_empcode)
    );
    setFilteredAlumni(availableAlumni);
  }, [alumni, existingContactIds]);

  // Unified filter logic
  const applyFilters = (term, role, companySize, projectSize, industry, techAlignment, lastContact) => {
    const filtered = alumni.filter(alumnus => {
      // Exclude already added
      if (existingContactIds.includes(alumnus.emp_empcode)) return false;
      // Search
      if (term && !(
        (alumnus.emp_empname && alumnus.emp_empname.toLowerCase().includes(term.toLowerCase())) ||
        (alumnus.emp_email && alumnus.emp_email.toLowerCase().includes(term.toLowerCase())) ||
        (alumnus.currentTitle && alumnus.currentTitle.toLowerCase().includes(term.toLowerCase())) ||
        (alumnus.currentCompany && alumnus.currentCompany.toLowerCase().includes(term.toLowerCase()))
      )) return false;
      // Advanced filters
      if (role && alumnus.currentTitle !== role) return false;
      if (companySize && alumnus.companySize !== companySize) return false;
      if (projectSize && alumnus.projectSize !== projectSize) return false;
      if (industry && alumnus.industry !== industry) return false;
      if (techAlignment && alumnus.techAlignment !== techAlignment) return false;
      if (lastContact && !isLastContactMatch(alumnus.lastContact, lastContact)) return false;
      return true;
    });
    setFilteredAlumni(filtered);
  };

  // Handle search
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    applyFilters(
      term,
      selectedRole,
      selectedCompanySize,
      selectedProjectSize,
      selectedIndustry,
      selectedTechAlignment,
      selectedLastContact
    );
  };

  // Handle filter dropdowns
  const handleFilterChange = (filterSetter) => (e) => {
    const value = e.target.value;
    filterSetter(value);
    applyFilters(
      searchTerm,
      filterSetter === setSelectedRole ? value : selectedRole,
      filterSetter === setSelectedCompanySize ? value : selectedCompanySize,
      filterSetter === setSelectedProjectSize ? value : selectedProjectSize,
      filterSetter === setSelectedIndustry ? value : selectedIndustry,
      filterSetter === setSelectedTechAlignment ? value : selectedTechAlignment,
      filterSetter === setSelectedLastContact ? value : selectedLastContact
    );
  };

  // When alumni or filters change, re-apply filters
  useEffect(() => {
    applyFilters(
      searchTerm,
      selectedRole,
      selectedCompanySize,
      selectedProjectSize,
      selectedIndustry,
      selectedTechAlignment,
      selectedLastContact
    );
    // eslint-disable-next-line
  }, [alumni, existingContactIds]);

  // Toggle contact selection
  const toggleContactSelection = (alumnus) => {
    if (isSelected(alumnus)) {
      setSelectedContacts(selectedContacts.filter(
        contact => contact.emp_empcode !== alumnus.emp_empcode
      ));
    } else {
      setSelectedContacts([...selectedContacts, alumnus]);
    }
  };

  // Check if a contact is selected
  const isSelected = (alumnus) => {
    return selectedContacts.some(
      contact => contact.emp_empcode === alumnus.emp_empcode
    );
  };

  // Select all filtered contacts
  const selectAll = () => {
    setSelectedContacts([...selectedContacts, ...filteredAlumni.filter(
      alumnus => !isSelected(alumnus)
    )]);
  };

  // Deselect all contacts
  const deselectAll = () => {
    setSelectedContacts([]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 overflow-hidden">
        <div className="bg-primary text-white px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold">Add Contacts to Campaign</h3>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <div className="p-6">
          {/* Search and filter */}
          <div className="mb-4 flex justify-between items-center">
            <div className="w-full md:w-1/2">
              <input
                type="text"
                placeholder="Search alumni..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
              >
                Deselect All
              </button>
            </div>
          </div>
          
          {/* Selected count */}
          <div className="mb-4 text-sm text-gray-600">
            {selectedContacts.length} contacts selected
          </div>
          
          {/* Alumni list */}
          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
            {filteredAlumni.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={filteredAlumni.length > 0 && filteredAlumni.every(alumnus => isSelected(alumnus))}
                        onChange={() => {
                          if (filteredAlumni.every(alumnus => isSelected(alumnus))) {
                            // Deselect all filtered alumni
                            setSelectedContacts(selectedContacts.filter(
                              contact => !filteredAlumni.some(
                                alumnus => alumnus.emp_empcode === contact.emp_empcode
                              )
                            ));
                          } else {
                            // Select all filtered alumni
                            selectAll();
                          }
                        }}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAlumni.map((alumnus) => (
                    <tr 
                      key={alumnus.emp_empcode} 
                      className={`hover:bg-gray-50 cursor-pointer ${isSelected(alumnus) ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleContactSelection(alumnus)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={isSelected(alumnus)}
                          onChange={() => toggleContactSelection(alumnus)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{alumnus.emp_empname}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{alumnus.emp_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{alumnus.currentTitle || 'Unknown'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{alumnus.currentCompany || 'Unknown'}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-8 text-center text-gray-500">
                {searchTerm ? 'No matching alumni found' : 'No available alumni to add'}
              </div>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(selectedContacts)}
              disabled={selectedContacts.length === 0}
              className={`px-4 py-2 rounded-md ${
                selectedContacts.length > 0
                  ? 'bg-primary text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Add {selectedContacts.length} Contacts
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContactsModal;
